import { useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

import {
  DataTemplateResponse,
  DynamicValueResponse,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";
import { evaluateDependentCondition } from "@/domains/datablocks/fields/validators";
import { DataBlockFormFields } from "@/domains/datablocks/types";

/**
 * Hook to determine which fields should be hidden based on dependent validation rules
 * Returns a Set of field IDs that should be hidden
 */
export const useFieldVisibility = (
  dataTemplate: DataTemplateResponse,
  control: Control<DataBlockFormFields>,
): Set<number> => {
  const visibilityRules = useMemo(() => {
    return dataTemplate.validationRules.filter(
      (rule) =>
        rule.type === ValidationRuleTypes.DEPENDENT_REQUIRED &&
        rule.options?.alwaysVisible !== true,
    );
  }, [dataTemplate.validationRules]);

  const fieldValuesArray = useWatch({
    control,
    name: "fieldValues",
  });

  return useMemo(() => {
    const hidden = new Set<number>();

    if (!fieldValuesArray || visibilityRules.length === 0) {
      return hidden;
    }

    const fieldValuesDict = fieldValuesArray.reduce<
      Record<number, DynamicValueResponse>
    >((acc, fv) => {
      if (fv && typeof fv.field === "number") {
        acc[fv.field] = fv.value;
      }
      return acc;
    }, {});

    visibilityRules.forEach((rule) => {
      if (!rule.dependentOn) return;

      const triggerFieldIds = Object.keys(rule.dependentOn.value).map(Number);
      let anyConditionMet = false;

      for (const triggerFieldId of triggerFieldIds) {
        const providedValue = fieldValuesDict[triggerFieldId];
        const expectedValue = rule.dependentOn.value[triggerFieldId];

        const conditionMet = evaluateDependentCondition(
          expectedValue,
          providedValue,
        );

        if (conditionMet) {
          anyConditionMet = true;
          break;
        }
      }

      if (!anyConditionMet) {
        rule.ruleFields?.forEach((fieldId) => hidden.add(fieldId));
      }
    });

    return hidden;
  }, [fieldValuesArray, visibilityRules]);
};
